import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type ColorMode = 'light' | 'dark';

interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  toggleColorMode: () => {},
});

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}

export function ColorModeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setMode] = useState<ColorMode>(() => {
    return (localStorage.getItem('colorMode') as ColorMode | null) ?? 'light';
  });

  useEffect(() => {
    localStorage.setItem('colorMode', mode);
    document.documentElement.setAttribute('data-bs-theme', mode);
  }, [mode]);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  }, []);

  const value = useMemo(() => ({ mode, toggleColorMode }), [mode, toggleColorMode]);

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}
